import Model from "../../Model.js";

/**
 * Unique identifier of a series specification.
 *
 * @typedef {object} SeriesSpecificationKey
 * @property {string} id - Unique identifier of this computation
 *   reference (e.g., keyFigure id, stable row id, hard coded id)
 * @property {"RowSeriesSpecification"|"KeyFigureSeriesSpecification"|"PeriodRollingSeriesSpecification"} type -
 *   Corresponding type of the {@link AbstractSeriesSpecification}
 *   this key refers to
 * @property {"OpeningBalance"|"ClosingBalance"|"Change"|"YearToDate"} operationType -
 */

/**
 * Abstract series specification for a Forecast computation.
 *
 * @abstract
 * @param {SeriesSpecificationKey} key - unique identifier of this
 *   series specification
 * @param {AbstractFormulaNode} node - The formula used to compute the
 *   series value
 * @param {"OpeningBalance"|"ClosingBalance"|"Change"|"YearToDate"} operationType - Account operation performed as computation
 * @param {number[]} [values] - Values fetched for this series
 *   specification
 */
export default class AbstractSeriesSpecification extends Model {
	constructor({ key, node, operationType, values }) {
		super(...arguments);

		this._key = key;
		this._node = node;
		this._operationType = operationType;
		this._values = values;
	}

	getKey() {
		return this._key;
	}

	getValues() {
		return this._values;
	}

	getOperationType() {
		return this._operationType;
	}

	_serializedProperties() {
		const schema = super._serializedProperties();

		schema.key = this._key;
		schema.node = this._node.serialize();
		schema.operationType = this._operationType;

		return schema;
	}

	static makeFromModel(...rest) {
		return new this({
			key: this.makeKeyFromModel(...rest),
			node: this.makeNodeFromModel(...rest),
			operationType: this.makeOperationTypeFromModel(...rest),
		});
	}

	static makeKeyFromModel(...rest) {
		const keyData = this._makeKeyDataFromModel(...rest);
		return this._makeKey(keyData);
	}

	static makeNodeFromModel() {
		this._subclassResponsibility();
	}

	static makeOperationTypeFromModel({ operationType }) {
		return operationType;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(data, context);

		data.operationType = data.key.operationType;
	}

	static getType() {
		this._subclassResponsibility();
	}

	static _makeKeyDataFromModel(data) {
		return data;
	}

	static _makeKey({ id, operationType }) {
		return {
			id,
			type: this.getType(),
			operationType,
		};
	}
}
